import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Video, Link2, Upload, Trash2, Loader2, Youtube } from "lucide-react";
import { toast } from "sonner";

export function parseVideo(url) {
  const u = (url || "").trim();
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { kind: "iframe", src: `https://www.youtube.com/embed/${yt[1]}`, poster: `https://img.youtube.com/vi/${yt[1]}/hqdefault.jpg` };
  const vm = u.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return { kind: "iframe", src: `https://player.vimeo.com/video/${vm[1]}` };
  return { kind: "video", src: u };
}

export default function VideoManager({ value = [], onChange, max = 4 }) {
  const items = Array.isArray(value) ? value : [];
  const [link, setLink] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const addLink = () => {
    const u = link.trim();
    if (!u) return;
    if (!/^https?:\/\//i.test(u)) { toast.error("Collez un lien valide (https://…)"); return; }
    if (items.length >= max) { toast.error(`Maximum ${max} vidéos.`); return; }
    onChange([...items, u]);
    setLink("");
    toast.success("Vidéo ajoutée");
  };

  const uploadFile = async (file) => {
    if (!file) return;
    if (items.length >= max) { toast.error(`Maximum ${max} vidéos.`); return; }
    if (!/^video\//.test(file.type)) { toast.error("Fichier vidéo requis (MP4, MOV, WEBM)."); return; }
    if (file.size > 60 * 1024 * 1024) { toast.error("Vidéo trop lourde (max 60 Mo)."); return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/pro/fb-posts/upload-media", fd, {
        onUploadProgress: (e) => { if (e.total) setProgress(Math.round((e.loaded / e.total) * 100)); },
      });
      onChange([...items, data.url]);
      toast.success("Vidéo téléversée");
    } catch (e) {
      toast.error(`Échec : ${e?.response?.data?.detail || e?.message || "erreur"}`);
    } finally { setUploading(false); setProgress(0); }
  };

  const removeAt = (i) => onChange(items.filter((_, k) => k !== i));

  return (
    <div data-testid="video-manager">
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <Link2 size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={link} onChange={(e) => setLink(e.target.value)}
                 onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLink())}
                 placeholder="Collez un lien YouTube ou Vimeo…" className="pl-9" data-testid="pro-video-link-input" />
        </div>
        <Button type="button" variant="outline" onClick={addLink} data-testid="pro-video-add-link">
          <Youtube size={15} className="mr-1.5" /> Ajouter le lien
        </Button>
        <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} disabled={uploading} data-testid="pro-video-upload-btn">
          {uploading ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> {progress}%</> : <><Upload size={15} className="mr-1.5" /> Téléverser</>}
        </Button>
        <input ref={inputRef} type="file" accept="video/mp4,video/quicktime,video/webm" className="hidden"
               onChange={(e) => { uploadFile(e.target.files?.[0]); e.target.value = ""; }} data-testid="pro-video-file-input" />
      </div>
      <p className="mt-1.5 text-[11px] text-slate-500">
        Lien YouTube/Vimeo ou fichier MP4/MOV/WEBM (max 60 Mo) · jusqu'à {max} vidéos.
      </p>

      {items.length > 0 && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="pro-video-list">
          {items.map((url, i) => {
            const v = parseVideo(url);
            return (
              <div key={`${i}-${url}`} className="relative group aspect-video rounded-xl overflow-hidden border border-slate-200 bg-slate-900" data-testid={`pro-video-item-${i}`}>
                {v.kind === "iframe"
                  ? (v.poster
                      ? <img src={v.poster} alt="" className="w-full h-full object-cover" />
                      : <div className="w-full h-full grid place-items-center text-white/80"><Video size={24} /></div>)
                  : <video src={v.src} className="w-full h-full object-cover" muted />}
                <div className="absolute inset-0 grid place-items-center text-white/90 pointer-events-none">
                  <span className="w-9 h-9 rounded-full bg-black/55 grid place-items-center"><Video size={16} /></span>
                </div>
                <button type="button" onClick={() => removeAt(i)} data-testid={`pro-video-remove-${i}`}
                        className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/90 grid place-items-center text-rose-600 hover:bg-white shadow" aria-label="Retirer">
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
